import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "../api";
import { Modal } from "./ui";

const CHECKUP_STEPS = [
  ["1", "Compilazione dal sito (webhook)", "Lead creato subito con dati, consensi, segmento e leve fiscali."],
  ["2", "Email con l'esito — automatica e immediata", "Parte entro pochi secondi col testo composto dal sito (campo email_esito). Non passa dalla coda di approvazione."],
  ["3", "Notifica interna", "Campanella nel CRM + email ad assistenza@ con il riepilogo del contatto."],
  ["4", "Sequenza dedicata (in arrivo)", "5 email nei primi 14 giorni — i testi verranno caricati a breve."],
  ["5", "Newsletter dal 21° giorno", "Solo chi ha dato il consenso marketing. Mai più di un'email ogni 5 giorni, sommando tutti i flussi."],
];

const CONSENSI_STEPS = [
  ["1", "Richiesta di consenso", "Inviata in automatico a nuovi clienti/lead con email (aziende e anagrafica)."],
  ["2", "Promemoria dopo 30 giorni", "Un solo sollecito a chi non ha risposto."],
  ["3", "Conferma → newsletter", "Chi conferma entra nel funnel newsletter di pianificazione fiscale."],
];

function Steps({ steps, preview, links }) {
  return (
    <div>
      {steps.map(([n, title, desc]) => (
        <div key={n} style={{ display: "flex", gap: 12, marginBottom: 10 }}>
          <div style={{ minWidth: 26, height: 26, borderRadius: "50%", background: "var(--accent, #2a3f7d)", color: "#fff", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 13, fontWeight: 700 }}>{n}</div>
          <div style={{ flex: 1 }}>
            <div style={{ fontWeight: 600 }}>{title}</div>
            <div className="muted" style={{ fontSize: 13 }}>{desc}</div>
          </div>
        </div>
      ))}
      <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginTop: 8 }}>
        {preview}
        {links}
      </div>
    </div>
  );
}

function StatChips({ stats, labels }) {
  return (
    <div style={{ display: "flex", gap: 10, flexWrap: "wrap", margin: "12px 0" }}>
      {Object.entries(labels).map(([k, label]) => (
        <div key={k} className="seg-card" style={{ padding: "10px 16px", minWidth: 110 }} data-testid={`sf-stat-${k}`}>
          <div style={{ fontSize: 22, fontWeight: 800 }}>{stats?.[k] ?? 0}</div>
          <div className="muted" style={{ fontSize: 12 }}>{label}</div>
        </div>
      ))}
    </div>
  );
}

function EmailEditor({ onClose, onSaved }) {
  const [data, setData] = useState(null);
  const [saving, setSaving] = useState(false);
  useEffect(() => { api.get("/funnels/system/emails").then((r) => setData(r.data)).catch(() => onClose()); }, []);
  if (!data) return null;
  const save = async () => {
    setSaving(true);
    try {
      await api.put("/funnels/system/emails", { consent: data.consent });
      onSaved(); onClose();
    } catch (e) { alert("Errore: " + (e.response?.data?.detail || e.message)); }
    setSaving(false);
  };
  return (
    <Modal title="✏️ Modifica testi email dei funnel" onClose={onClose} wide>
      <h4 style={{ margin: "4px 0 10px" }}>🔐 Email richiesta consenso GDPR</h4>
      <label style={{ display: "block", marginBottom: 10 }}>
        <span className="muted" style={{ fontSize: 12 }}>Oggetto email</span>
        <input value={data.consent.subject || ""} onChange={(e) => setData((d) => ({ ...d, consent: { ...d.consent, subject: e.target.value } }))} style={{ width: "100%" }} data-testid="ee-consent-subject" />
      </label>
      <div style={{ display: "flex", gap: 8, justifyContent: "flex-end", marginTop: 14 }}>
        <button className="btn primary" onClick={save} disabled={saving} data-testid="ee-save">{saving ? "Salvo…" : "💾 Salva"}</button>
      </div>
    </Modal>
  );
}

export function FunnelSection({ title, subtitle, badge, defaultOpen = false, testid, children }) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className="panel" style={{ marginBottom: 14 }}>
      <div onClick={() => setOpen(!open)} style={{ display: "flex", alignItems: "center", gap: 12, cursor: "pointer" }} data-testid={testid}>
        <div style={{ flex: 1 }}>
          <h3 style={{ margin: 0 }}>{title}</h3>
          {subtitle && <div className="muted" style={{ fontSize: 12, marginTop: 2 }}>{subtitle}</div>}
        </div>
        {badge}
        <span style={{ fontSize: 18, opacity: 0.7 }}>{open ? "▾" : "▸"}</span>
      </div>
      {open && <div style={{ marginTop: 14 }}>{children}</div>}
    </div>
  );
}

export default function SystemFunnels() {
  const [rows, setRows] = useState([]);
  const [preview, setPreview] = useState(null);
  const [editor, setEditor] = useState(false);
  const load = () => api.get("/funnels/system").then((r) => setRows(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const get = (key) => rows.find((r) => r.key === key) || { enabled: true, stats: {} };
  const toggle = async (key, enabled) => {
    try { await api.post(`/funnels/system/${key}/toggle`, { enabled }); load(); }
    catch (e) { alert("Errore: " + (e.response?.data?.detail || e.message)); }
  };
  const openPreview = async (url) => {
    try { const r = await api.get(url); setPreview(r.data); } catch { alert("Anteprima non disponibile"); }
  };
  const badge = (f) => (
    <span className={"pill " + (f.enabled ? "gestita" : "da_gestire")}>{f.enabled ? "Attivo" : "In pausa"}</span>
  );
  const toggleRow = (key, f) => (
    <label style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 12 }} onClick={(e) => e.stopPropagation()}>
      <input type="checkbox" checked={f.enabled} onChange={(e) => toggle(key, e.target.checked)} data-testid={`sf-toggle-${key}`} />
      <strong style={{ color: f.enabled ? "#16a34a" : "#94a3b8" }}>{f.enabled ? "Funnel ATTIVO" : "Funnel in pausa"}</strong>
      <span className="muted" style={{ fontSize: 12 }}>{f.enabled ? "" : "— in pausa non parte nessuna email automatica"}</span>
    </label>
  );

  const checkup = get("checkup");
  const consensi = get("consensi");
  const cambio = get("cambio");
  const CAMBIO_STEPS = [
    ["1", "Form dal sito /cambiare-commercialista", "Contatto creato con consensi (privacy + contatto) datati, UTM del primo accesso e tag «lead cambio commercialista»."],
    ["2", "Sequenza 01→06", "Checklist subito, poi +2, +4, +6, +9 e +12 giorni. Parte con la sola privacy; si ferma col tag «colloquio fissato»."],
    ["3", "Prenotazione su /prenota", "Tag «colloquio fissato» + email 07 di conferma con data/ora/modalità, rimandata il giorno prima come promemoria."],
    ["4", "Giorno 13 senza prenotazione", "Con consenso al contatto: incarico «Chiamare» per lo studio con il link alla scheda."],
  ];
  return (
    <>
      <FunnelSection title="🔁 Funnel Cambio commercialista" subtitle="Form dal sito → checklist + sequenza 6 email → prenotazione → conferma incontro e promemoria"
        badge={badge(cambio)} testid="sf-section-cambio">
        {toggleRow("cambio", cambio)}
        <StatChips stats={cambio.stats} labels={{ lead_ricevuti: "Lead dal form", email_inviate: "Email inviate", colloqui_fissati: "Colloqui fissati 📅", da_chiamare: "Da chiamare ☎" }} />
        <Steps steps={CAMBIO_STEPS}
          preview={null}
          links={<><Link className="btn sm" to="/lead">Vedi i lead →</Link><Link className="btn sm" to="/template-email">Modelli email →</Link><Link className="btn sm" to="/da-approvare">Coda da approvare →</Link></>} />
      </FunnelSection>

      <FunnelSection title="🩺 Funnel Check-up fiscale" subtitle="Compilazione dal sito → lead + esito automatico → sequenza dedicata → newsletter dal 21° giorno"
        badge={badge(checkup)} testid="sf-section-checkup">
        {toggleRow("checkup", checkup)}
        <StatChips stats={checkup.stats} labels={{ checkup_ricevuti: "Check-up ricevuti", acquisti: "Acquisti dal sito 💶", in_attesa_newsletter: "In attesa newsletter", in_newsletter: "Entrati in newsletter" }} />
        <Steps steps={CHECKUP_STEPS}
          preview={null}
          links={<><Link className="btn sm" to="/lead">Vedi i lead →</Link><Link className="btn sm" to="/da-approvare">Coda da approvare →</Link></>} />
      </FunnelSection>

      <FunnelSection title="🔐 Funnel Consensi GDPR" subtitle="Richiesta consenso automatica → promemoria 30 giorni → iscrizione newsletter"
        badge={badge(consensi)} testid="sf-section-consensi">
        {toggleRow("consensi", consensi)}
        <StatChips stats={consensi.stats} labels={{ in_attesa: "In attesa", confermati: "Confermati", disiscritti: "Disiscritti" }} />
        <Steps steps={CONSENSI_STEPS}
          preview={<><button className="btn sm" onClick={() => openPreview("/optin/preview")} data-testid="sf-preview-consensi">👁 Anteprima richiesta consenso</button>
            <button className="btn sm" onClick={() => setEditor(true)} data-testid="sf-edit-emails-2">✏️ Modifica testi email</button></>}
          links={<Link className="btn sm" to="/consensi">Gestisci consensi →</Link>} />
      </FunnelSection>

      {editor && <EmailEditor onClose={() => setEditor(false)} onSaved={load} />}

      {preview && (
        <Modal title={`Anteprima — ${preview.subject}`} onClose={() => setPreview(null)} wide>
          <iframe title="anteprima-email" sandbox="" srcDoc={preview.html} data-testid="sf-preview-iframe"
            style={{ width: "100%", height: "70vh", border: "1px solid var(--line, #22222e)", borderRadius: 10, background: "#fff" }} />
        </Modal>
      )}
    </>
  );
}
